/**
 * <product-gallery> — the product image gallery.
 *
 * Wraps two Swiper instances: the main image slider and a thumbnail strip, linked with
 * Swiper's `thumbs` module so selecting a thumbnail moves the main slider and vice
 * versa. Navigation uses the prev/next controls exported from Figma rather than
 * Swiper's own arrows, so they can be styled from tokens.
 *
 * Swiper is initialised inside the element (never globally) and only on templates that
 * render this section, so no other page pays for it.
 */

// Wrapped so nothing here reaches global scope: these are classic scripts, and a
// top-level name shared with another file makes the whole file throw before it can
// register its element. Also what makes a duplicate <script> tag for this file safe.
(() => {
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      // The bundle is loaded with `defer`, so it may not have executed yet.
      if (typeof window.Swiper === 'undefined') {
        window.addEventListener('load', () => this.init(), { once: true });
        return;
      }

      this.init();
    }

    disconnectedCallback() {
      this.main?.destroy(true, true);
      this.thumbs?.destroy(true, true);
    }

    init() {
      if (typeof window.Swiper === 'undefined') return;

      const mainElement = this.querySelector('[data-gallery-main]');
      if (!mainElement) return;

      const thumbsElement = this.querySelector('[data-gallery-thumbs]');

      if (thumbsElement) {
        this.thumbs = new window.Swiper(thumbsElement, {
          slidesPerView: 'auto',
          spaceBetween: 10,
          freeMode: true,
          watchSlidesProgress: true,
          // Thumbnails are <button>s; let arrow keys move between them naturally.
          a11y: { enabled: false },
        });

        const thumbsPrev = this.querySelector('[data-thumbs-prev]');
        const thumbsNext = this.querySelector('[data-thumbs-next]');

        thumbsPrev?.addEventListener('click', () => this.thumbs.slidePrev());
        thumbsNext?.addEventListener('click', () => this.thumbs.slideNext());

        const wrap = this.querySelector('[data-thumbs-wrap]');

        // Hide each control at the end of its travel: prev stays out of the way until
        // the strip has actually been scrolled, rather than sitting there disabled. The
        // edge fades follow the same state — a fade with no control behind it just looks
        // like the thumbnails are clipped.
        const syncThumbNav = () => {
          const atStart = this.thumbs.isBeginning;
          const atEnd = this.thumbs.isEnd;

          if (thumbsPrev) thumbsPrev.hidden = atStart;
          if (thumbsNext) thumbsNext.hidden = atEnd;

          wrap?.classList.toggle('is-start', atStart);
          wrap?.classList.toggle('is-end', atEnd);
        };

        this.thumbs.on('fromEdge', syncThumbNav);
        this.thumbs.on('progress', syncThumbNav);
        this.thumbs.on('reachBeginning', syncThumbNav);
        this.thumbs.on('reachEnd', syncThumbNav);
        this.thumbs.on('resize', syncThumbNav);
        syncThumbNav();
      }

      this.main = new window.Swiper(mainElement, {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: false,
        navigation: {
          prevEl: this.querySelector('[data-gallery-prev]'),
          nextEl: this.querySelector('[data-gallery-next]'),
        },
        keyboard: { enabled: true, onlyInViewport: true },
        thumbs: this.thumbs ? { swiper: this.thumbs } : undefined,
        a11y: {
          enabled: true,
          prevSlideMessage: this.querySelector('[data-gallery-prev]')?.getAttribute('aria-label'),
          nextSlideMessage: this.querySelector('[data-gallery-next]')?.getAttribute('aria-label'),
        },
      });
    }
  }

  if (!customElements.get('product-gallery')) {
    customElements.define('product-gallery', ProductGallery);
  }
})();
